import React, { useEffect } from "react";
import ReactDOM from "react-dom";
//#region types
import { TwoDChartProps } from "models/propTypes/chartProps";
import { GraphConstants } from "utils/constants";
import { ChartEnums } from "models/enums";
//#endregion
//#region components
import Chart, { ChartDataSets, ChartPoint } from "chart.js";
import { Grid } from "@mui/material";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { ChartHelper, LocalizationHelperInstance } from "utils/helpers";
import useChartContext from "./context/useContext";
import { createFreezeTooltipPlugin } from "./utils/freezeTooltipPlugin";
import { registerOutsideClickClear } from "./utils/outsideClick";

export default function LineChart(props: React.PropsWithChildren<TwoDChartProps>) {
  const chartCanvasId = `${GraphConstants.LineChartIdPrefix}${props.id}`;
  const chartLegendId = `${GraphConstants.LegendIdPrefix}${GraphConstants.LineChartIdPrefix}${props.id}`;
  let legendPortalContainer = props.legendPortalId ? document.getElementById(props.legendPortalId) : null;

  const lineWidth = 4;
  const selectedPointIndexRef = React.useRef<number | null>(null);
  const selectedDatasetIndexRef = React.useRef<number | null>(null);
  const frozenRef = React.useRef<boolean>(false);
  const chartRef = React.useRef<any>(null);
  const justSetSelectionRef = React.useRef<number | null>(null);

  const chartContext = useChartContext();

  React.useEffect(() => {
    if (legendPortalContainer) {
      if (legendPortalContainer.childElementCount > 1 && legendPortalContainer.firstChild)
        legendPortalContainer.removeChild(legendPortalContainer.firstChild);
    }

    return () => {
      legendPortalContainer = null;
    };
  }, []);

  const getFreezePlugin = () =>
    createFreezeTooltipPlugin({
      id: `${chartCanvasId}-freeze`,
      canvasId: chartCanvasId,
      isFrozenRef: frozenRef,
      hasSelectionRef: selectedPointIndexRef as any,
      getActiveElement: (chart: any) => {
        const dsi = selectedDatasetIndexRef.current ?? 0;
        return chart.getDatasetMeta(dsi)?.data?.[selectedPointIndexRef.current as any];
      },
      withBeforeUpdate: true,
      withAfterDraw: true,
      drawOnAfterEvent: true,
    });

  React.useEffect(() => {
    const freezePluginId = `${chartCanvasId}-freeze`;
    try {
      const existing = Chart.pluginService.getAll().find((c) => c.id === freezePluginId);
      existing && Chart.pluginService.unregister(existing);
    } catch {}
    try {
      Chart.pluginService.register(getFreezePlugin());
    } catch {}
    return () => {
      try {
        const existing = Chart.pluginService.getAll().find((c) => c.id === freezePluginId);
        existing && Chart.pluginService.unregister(existing);
      } catch {}
    };
  }, [chartCanvasId]);

  useEffect(() => {
    let ctx: any = document.getElementById(chartCanvasId);

    const chart = new Chart(ctx, {
      type: "line",
      data: {
        labels: props.labels,
        datasets: getData(),
      },
      options: {
        responsive: true,
        maintainAspectRatio: props.maintainAspectRatio ?? false,
        legend: {
          display: false,
        },
        legendCallback: function (chart) {
          return ChartHelper.generateLegend(chart, "border");
        },
        layout: {
          padding: {
            left: -5,
            right: 8,
          },
        },
        onClick: function (event, activeElements) {
          if (!props.useClickableChart) return;
          if (activeElements !== undefined && activeElements[0]) {
            const datasetIndex = (activeElements[0] as any)._datasetIndex;
            const chartData = chart.config.data;
            const index = (activeElements[0] as any)._index;

            const label = chartData?.labels !== undefined ? chartData.labels[index] : undefined;

            const dataSet = chartData?.datasets !== undefined ? chartData.datasets[datasetIndex] : undefined;
            // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
            const xAxisPoint = dataSet?.data != undefined ? (dataSet.data[index] as Chart.ChartPoint)?.x : undefined;

            // Freeze tooltip on the selected point
            try {
              try {
                chartContext?.setSelectedChartId && chartContext.setSelectedChartId(String(props.id));
              } catch {}
              selectedPointIndexRef.current = index;
              selectedDatasetIndexRef.current = datasetIndex;

              try {
                chartContext?.setSelection && chartContext.setSelection(String(props.id), { index, datasetIndex });
              } catch {}

              frozenRef.current = true;
              justSetSelectionRef.current = Date.now();

              try {
                const meta = chart.getDatasetMeta(datasetIndex);
                const activePoint = meta?.data?.[index];
                if (activePoint) {
                  (chart as any).tooltip._active = [activePoint];
                  (chart as any).tooltip.update(true);
                }
              } catch {}

              try {
                chart.update();
              } catch {}
            } catch {}

            if (props.onClick) props.onClick(index, datasetIndex, xAxisPoint, label);
          }
        },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                fontColor: GraphConstants.BarLegendFontColor,
                fontSize: props.overrideLabelFontSize ?? 12,
                fontFamily: "MuseoSans-700",
                padding: 8,
                maxTicksLimit: 4,
                callback: function (value, index, values) {
                  if (props.yAxisTickUnit === ChartEnums.YAxisTickUnitType.Currency) {
                    if (typeof value === "number") {
                      return ChartHelper.validateFormatChartYAxis(value, values);
                    }
                  }
                  return value;
                },
              },
              gridLines: {
                borderDash: [3, 2],
                drawBorder: false,
                color: GraphConstants.BarGridLinesColor,
                zeroLineColor: GraphConstants.BarAxisColor,
                zeroLineWidth: 2,
                lineWidth: 1,
              },
            },
          ],
          xAxes: [
            {
              ticks: {
                autoSkip: true,
                backdropColor: GraphConstants.BarBackdropColor,
                fontColor: GraphConstants.BarLegendFontColor,
                fontSize: props.overrideLabelFontSize ?? 12,
                fontFamily: "MuseoSans-700",
                maxRotation: 0,
                callback: ChartHelper.getDisplayLabel,
              },
              distribution: "series",
              gridLines: {
                display: false,
                color: GraphConstants.BarGridLinesColor,
                tickMarkLength: 8,
              },
              type: "category",
            },
          ],
        },
        elements: {
          point: {
            pointStyle: "circle",
          },
        },
        hover: {
          mode: "nearest",
          intersect: false,
          onHover: function (event, activeElements) {
            ctx.style.cursor = activeElements[0] ? "pointer" : "default";
          },
        },
        tooltips: {
          mode: "nearest",
          intersect: false,
          backgroundColor: GraphConstants.TooltipBackgroundColor,
          titleFontColor: GraphConstants.TooltipTitleFontColor,
          titleFontSize: GraphConstants.TooltipTitleFontSize,
          bodyFontColor: GraphConstants.TooltipBodyFontColor,
          bodyFontSize: GraphConstants.TooltipBodyFontSize,
          displayColors: false,
          callbacks: {
            title: function (items, data) {
              if (
                data !== undefined &&
                items !== undefined &&
                data.labels !== undefined &&
                items[0].index !== undefined
              )
                return data.labels[items[0].index]?.toString();
              else return "";
            },
            label: function (tooltipItem, data) {
              const index = tooltipItem.index ?? 0;
              const datasetIndex = tooltipItem.datasetIndex ?? 0;
              // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
              const dataset =
                data !== undefined && data?.datasets !== undefined ? data?.datasets[datasetIndex] : undefined;

              const chartPoint =
                // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
                index !== undefined && dataset !== undefined && dataset.data !== undefined ? dataset.data[index] : 0;

              const y = (chartPoint as ChartPoint).y;
              return `${
                typeof y === "number" ? LocalizationHelperInstance.getFormattedNumber(y ?? 0, 0, 2) : y?.toString()
              } ${props.dataUnit ?? GraphConstants.DefaultDataUnit}`;
            },
          },
        },
      },
    });

    configureLegendClick(
      (legendItem: HTMLLIElement) => legendItem.addEventListener("click", legendClickCallback, false),
      chart
    );

    // Re-apply selection if stored in React context
    try {
      const stored = chartContext?.getSelection ? chartContext.getSelection(String(props.id)) : null;
      if (stored != null) {
        const storedIndex = stored.index;
        const storedDatasetIndex = stored.datasetIndex ?? 0;
        selectedPointIndexRef.current = storedIndex;
        selectedDatasetIndexRef.current = storedDatasetIndex;
        frozenRef.current = true;

        try {
          const meta = chart.getDatasetMeta(storedDatasetIndex);
          const activePoint = meta?.data?.[storedIndex];
          if (activePoint) {
            (chart as any).tooltip._active = [activePoint];
            (chart as any).tooltip.update(true);
          }
        } catch {}
        try {
          chart.update();
        } catch {}
      }
    } catch {}

    chartRef.current = chart;

    return () => {
      ctx = null;
      try {
        chart.destroy();
      } catch {}

      configureLegendClick((legendItem: HTMLLIElement) =>
        legendItem.removeEventListener("click", legendClickCallback, false)
      );
    };
  }, [props]);

  React.useEffect(() => {
    const selected = chartContext?.selectedChartId;
    // If selection is cleared globally (e.g., Clear filter), clear this chart immediately
    if (!selected) {
      if (chartRef.current && (frozenRef.current || selectedPointIndexRef.current != null)) {
        try {
          const chart = chartRef.current as any;
          selectedPointIndexRef.current = null;
          selectedDatasetIndexRef.current = null;
          frozenRef.current = false;
          justSetSelectionRef.current = null;
          try {
            chart.tooltip._active = [];
            chart.tooltip.update(true);
          } catch {}
          try {
            chartContext?.setSelection && chartContext.setSelection(String(props.id), null);
          } catch {}
          try {
            chart.update();
          } catch {}
        } catch {}
      }
      return;
    }
    if (selected === props.id) return;
    // If this chart was just clicked (grace period), ignore immediate context changes from other charts
    try {
      const now = Date.now();
      const lastSetAt = justSetSelectionRef.current;
      const withinGrace = lastSetAt != null && now - lastSetAt < 600;
      const hasStoredSelection = !!(chartContext?.getSelection && chartContext.getSelection(String(props.id)));
      if (withinGrace && hasStoredSelection && frozenRef.current && selectedPointIndexRef.current != null) {
        return;
      }
    } catch {}
    if (chartRef.current) {
      try {
        const chart = chartRef.current as any;
        selectedPointIndexRef.current = null;
        selectedDatasetIndexRef.current = null;
        frozenRef.current = false;
        justSetSelectionRef.current = null;
        try {
          chart.tooltip._active = [];
          chart.tooltip.update(true);
        } catch {}
        try {
          chartContext?.setSelection && chartContext.setSelection(String(props.id), null);
        } catch {}
        try {
          chart.update();
        } catch {}
      } catch {}
    }
  }, [chartContext?.selectedChartId, props.id]);

  React.useEffect(() => {
    const unregister = registerOutsideClickClear({
      canvasId: chartCanvasId,
      legendId: chartLegendId,
      resolvePanelRoot: (canvas) => {
        const root =
          canvas && canvas.closest ? (canvas.closest(`.${cssExports.viewPanelOuter}`) as HTMLElement | null) : null;
        return (root ? root.parentElement : null) as HTMLElement | null;
      },
      insideCanvasHandling: "ignore",
      shouldSkip: () => !props.useClickableChart || (!frozenRef.current && selectedPointIndexRef.current == null),
      onClear: () => {
        if (chartRef.current) {
          try {
            const chart = chartRef.current as any;
            selectedPointIndexRef.current = null;
            selectedDatasetIndexRef.current = null;
            frozenRef.current = false;
            justSetSelectionRef.current = null;
            try {
              chart.tooltip._active = [];
              chart.tooltip.update(true);
            } catch {}
            try {
              chartContext?.setSelection && chartContext.setSelection(String(props.id), null);
            } catch {}
            try {
              chart.update();
            } catch {}
            try {
              if (props.onBackgroundClick) props.onBackgroundClick();
            } catch {}
            try {
              chartContext?.setSelectedChartId && chartContext.setSelectedChartId(null);
            } catch {}
          } catch {}
        }
      },
    });
    return unregister;
  }, [chartCanvasId, chartLegendId, chartContext?.setSelectedChartId]);

  const getData = () => {
    const chartDataSets: ChartDataSets[] = [];
    const colors = GraphConstants.ChartsDefaultColorPalette;

    props.datasets?.forEach((dataset, index) => {
      const colorForDataset = colors[index];

      chartDataSets.push({
        label: ChartHelper.getLabel(dataset.label),
        data: typeof dataset.data === "number" ? undefined : dataset.data,
        backgroundColor: "transparent",
        borderColor: colorForDataset,
        pointHoverBackgroundColor: colorForDataset,
        borderWidth: lineWidth,
        borderJoinStyle: "round",
        pointRadius: 0,
      });
    });

    return chartDataSets;
  };

  const configureLegendClick = (callback: (legendItems: HTMLLIElement) => void, chart?: Chart) => {
    const myLegendContainer = document.getElementById(chartLegendId);
    // generate HTML legend
    if (myLegendContainer != null && chart) myLegendContainer.innerHTML = chart.generateLegend() as string;

    const legendItems = myLegendContainer?.getElementsByTagName("li");
    // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
    if (legendItems != undefined && legendItems.length > 1)
      for (let i = 0; i < legendItems.length; i += 1) {
        callback(legendItems[i]);
      }
  };

  function legendClickCallback(this: HTMLLIElement, event: MouseEvent) {
    ChartHelper.legendClickCallback(chartCanvasId, this);
  }

  function getLegendContainer() {
    if (legendPortalContainer) {
      return ReactDOM.createPortal(
        <Grid
          container
          id={chartLegendId}
          className={clsx(props.classes?.legend, cssExports.chartLegendPortal)}
          justifyContent="center"
        ></Grid>,
        legendPortalContainer
      );
    } else {
      return <Grid container id={chartLegendId} className={props.classes?.legend} justifyContent="center"></Grid>;
    }
  }

  return (
    <Grid container className={clsx(props.classes?.canvas, cssExports.chartComponentContainer)}>
      {props.title && (
        <Grid container justifyContent="center" className={cssExports.lineChartTitle}>
          {props.title}
        </Grid>
      )}
      <Grid container className={props.classes?.chartContainer}>
        <canvas id={chartCanvasId} />
      </Grid>
      {!props.hideLegend && getLegendContainer()}
    </Grid>
  );
}
